import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { EMPTY, from, Observable } from 'rxjs';
import { mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import * as actions from './data-layer.actions';
import * as fromDataLayer from './data-layer.reducer';

import { Analytics, isSupported, logEvent } from '@angular/fire/analytics';

@Injectable({
  providedIn: 'root',
})
export class DataLayerEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly injector: Injector
  ) { }

  tepView$ = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.StepView>(actions.STEP_VIEW),
      withLatestFrom(this.store$.select(fromDataLayer.getDataLayer)),
      mergeMap((actionAndStoreState) => this.sendData('step-view', actionAndStoreState)),
    ),
    { dispatch: false }
  );

  PopupView$ = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.PopupView>(actions.POPUP_VIEW),
      withLatestFrom(this.store$.select(fromDataLayer.getDataLayer)),
      mergeMap((actionAndStoreState) => this.sendData('popup', actionAndStoreState)),
    ),
    { dispatch: false }
  );

  private sendData(action: string, actionAndStoreState): Observable<never> {
    return from(this.getAnalytics()).pipe(
      tap((analytics) => {
        if (!analytics) {
          return;
        }

        const eventName = `${action}-${actionAndStoreState[0].name}`;
        const store = actionAndStoreState[1];

        Object.keys(store).forEach((key) => {
          logEvent(analytics, eventName, {
            [key]: store[key],
          });
        });
      }),
      mergeMap(() => EMPTY),
    );
  }

  private async getAnalytics(): Promise<Analytics | null> {
    const supported = await isSupported();

    if (!supported) {
      return null;
    }

    return this.injector.get(Analytics);
  }
}